import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Paperclip, Trash2 } from "lucide-react";
import { crTime, crDateTime, chatDayLabel } from "@/lib/dateFormat";
import { toast } from "sonner";
import { useI18n } from "@/i18n/i18n";

const COLORES = ["text-blue-700", "text-violet-700", "text-amber-700", "text-rose-700",
                 "text-teal-700", "text-indigo-700"];
const colorDe = (id = "") =>
  COLORES[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORES.length];

/** Chat del grupo: los compañeros y el profesor conversan en un mismo lugar. */
export default function GroupChat({ group, height = "min(65vh, 560px)" }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    if (!group?.group_id) return;
    try {
      const r = await api.get(`/chat/groups/${group.group_id}`);
      setMessages(r.data || []);
      api.post(`/chat/groups/${group.group_id}/mark-seen`).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh();
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.group_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const txt = text.trim();
    if (!txt && !file) return;
    setPosting(true);
    try {
      const r = await api.post(`/chat/groups/${group.group_id}`,
                               { text: txt || `📎 ${file.name}` });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/chat/groups/${group.group_id}/message/${r.data.id}/file`, fd,
                       { headers: { "Content-Type": "multipart/form-data" } });
      }
      setText(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gc_send_error"));
    } finally {
      setPosting(false);
    }
  };

  const borrar = async (m) => {
    try {
      await api.delete(`/chat/groups/${group.group_id}/message/${m.id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gc_send_error"));
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  return (
    <div className="flex flex-col" style={{ height }} data-testid="group-chat">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-sky-50/40 rounded-lg"
           data-testid="gc-messages">
        {messages.length === 0 && (
          <div className="text-center py-10 text-sm text-slate-500 italic">{t("gc_empty")}</div>
        )}
        {messages.map((m, i) => {
          const isMe = m.author_user_id === user.user_id;
          const prev = messages[i - 1];
          const newDay = !prev || chatDayLabel(prev.created_at) !== chatDayLabel(m.created_at);
          return (
            <div key={m.id}>
              {newDay && (
                <div className="flex justify-center my-3">
                  <span className="bg-white/90 border border-slate-200 text-slate-600 text-[11px] font-semibold px-3 py-1 rounded-full shadow-sm">
                    {chatDayLabel(m.created_at)}
                  </span>
                </div>
              )}
              <div className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                   data-testid={`gc-msg-${m.id}`}>
                <div className={`max-w-[80%] border rounded-lg p-3 shadow-sm ${
                  isMe ? "bg-emerald-100 border-emerald-200 rounded-tr-none"
                       : "bg-white border-slate-200 rounded-tl-none"}`}>
                  {!isMe && (
                    <div className={`text-[10px] font-bold mb-0.5 ${colorDe(m.author_user_id)}`}>
                      {m.author_name || "—"}
                      {m.author_role === "teacher" && ` · ${t("gc_teacher")}`}
                      {(m.author_role === "superadmin" || m.author_role === "academic_director")
                        && ` · ${t("dm_sec_staff")}`}
                    </div>
                  )}
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.attachments.map((f) => (
                        <div key={f.id} className="inline-flex items-center gap-1.5 bg-white/70 border border-slate-200 rounded-md px-2 py-1 text-[11px]">
                          <Paperclip className="h-3 w-3 text-slate-500" />
                          <span className="text-slate-800 truncate max-w-[160px]" title={f.original_filename}>{f.original_filename}</span>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/view`}
                             target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">ver</a>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`text-[10px] mt-1.5 flex items-center gap-2 ${isMe ? "justify-end text-emerald-700" : "text-slate-400"}`}>
                    <span title={crDateTime(m.created_at)}>{crTime(m.created_at)}</span>
                    {isMe && (
                      <button type="button" onClick={() => borrar(m)} className="hover:text-rose-600"
                              data-testid={`gc-del-${m.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200 bg-slate-50 p-2.5 mt-2 rounded-b-lg flex items-center gap-2">
        <input ref={fileRef} type="file" className="hidden" data-testid="gc-file-input"
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="gc-file-btn"
                className="h-9 w-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-600">
          <Paperclip className="h-4 w-4" />
        </button>
        {file && (
          <span className="text-[11px] text-slate-600 truncate max-w-[140px] bg-slate-100 px-2 py-0.5 rounded">
            {file.name}
            <button className="ml-1 text-rose-600"
                    onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>✕</button>
          </span>
        )}
        <Input value={text} onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
               placeholder={t("gc_placeholder").replace("{g}", group?.group_name || "")}
               className="flex-1 h-10 rounded-full px-4 border-slate-200" data-testid="gc-input" />
        <Button onClick={send} disabled={posting || (!text.trim() && !file)}
                className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 p-0"
                data-testid="gc-send-btn">
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
